import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { forwardRef } from 'react';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string | React.ReactNode;
  error?: string;
}

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  flex-direction: row;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const StyledCheckbox = styled.div<{ checked?: boolean; hasError?: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border: 2px solid ${({ hasError, checked }) => 
    hasError ? theme.colors.error : checked ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${({ checked }) => checked ? theme.colors.primary : theme.colors.background};
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border-color: ${({ hasError }) => hasError ? theme.colors.error : theme.colors.primary};
  }

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 3px ${({ hasError }) => 
      hasError ? 'rgba(231, 76, 60, 0.1)' : 'rgba(255, 107, 157, 0.1)'};
  }

  ${HiddenCheckbox}:disabled + & {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::after {
    content: '';
    display: ${({ checked }) => checked ? 'block' : 'none'};
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-bottom: 2px;
  }
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  flex: 1;
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text};
  cursor: pointer;
  text-align: right;
  user-select: none;
`;

const ErrorText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.error};
  text-align: right;
`;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, checked, id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    
    return (
      <div>
        <CheckboxWrapper>
          <label htmlFor={checkboxId}>
            <HiddenCheckbox ref={ref} id={checkboxId} checked={checked} {...props} />
            <StyledCheckbox checked={checked} hasError={!!error} />
          </label>
          {label && (
            <LabelWrapper>
              <Label htmlFor={checkboxId}>{label}</Label>
            </LabelWrapper>
          )}
        </CheckboxWrapper>
        {error && <ErrorText>{error}</ErrorText>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
